/**
 * Utility functions for mapping between gender display formats and catalog formats
 */

export type CatalogGender = "women" | "men" | "kids" | "all";
export type DisplayGender = "Women's" | "Men's" | "Kids" | "Both (M+W)" | "Beauty";
export type CategoryToken = "women's" | "men's" | "kids" | "beauty";

export const DISPLAY_GENDER_OPTIONS: DisplayGender[] = [
  "Women's",
  "Men's",
  "Both (M+W)",
  "Kids",
  "Beauty",
];

const DISPLAY_GENDER_TO_CATEGORY_TOKEN: Record<DisplayGender, CategoryToken | undefined> = {
  "Women's": "women's",
  "Men's": "men's",
  Kids: "kids",
  "Both (M+W)": undefined,
  Beauty: "beauty",
} as const;

const EDITORIAL_CATEGORY_TO_TOKEN: Record<
  "Womenswear" | "Menswear" | "Kidswear" | "Beauty",
  CategoryToken
> = {
  Womenswear: "women's",
  Menswear: "men's",
  Kidswear: "kids",
  Beauty: "beauty",
};

/**
 * Maps catalog gender format to display format
 */
export function mapGenderToDisplay(gender: string): DisplayGender {
  switch (gender) {
    case "women":
      return "Women's";
    case "men":
      return "Men's";
    case "kids":
      return "Kids";
    case "all":
      return "Both (M+W)";
    case "beauty":
      return "Beauty";
    default:
      return "Women's";
  }
}

/**
 * Maps display gender format to catalog format
 */
export function mapGenderToCatalog(gender: string | undefined): CatalogGender | undefined {
  if (!gender) return undefined;

  switch (gender) {
    case "Women's":
      return "women";
    case "Men's":
      return "men";
    case "Kids":
      return "kids";
    case "Both (M+W)":
      return "all";
    case "Beauty":
      return undefined;
    default:
      // If it's already in catalog format
      if (gender === "men" || gender === "women" || gender === "kids" || gender === "all") {
        return gender as CatalogGender;
      }
      if (gender === "beauty") {
        return undefined;
      }
      return "women"; // default fallback
  }
}

/**
 * Checks if a gender string is in catalog format
 */
export function isCatalogGender(gender: string): gender is CatalogGender {
  return gender === "women" || gender === "men" || gender === "kids" || gender === "all";
}

/**
 * Checks if a gender string is in display format
 */
export function isDisplayGender(gender: string): gender is DisplayGender {
  return (
    gender === "Women's" ||
    gender === "Men's" ||
    gender === "Kids" ||
    gender === "Both (M+W)" ||
    gender === "Beauty"
  );
}

/**
 * Convert a display gender label into an agent category token.
 * Returns undefined for labels without a dedicated token (e.g. Both (M+W)).
 */
export function displayGenderToCategoryToken(
  gender: string | undefined
): CategoryToken | undefined {
  if (!gender) return undefined;
  if (isDisplayGender(gender)) {
    return DISPLAY_GENDER_TO_CATEGORY_TOKEN[gender];
  }
  return undefined;
}

/**
 * Convert editorial section labels (e.g. Womenswear) into agent category tokens.
 */
export function editorialCategoryToToken(category: string | undefined): CategoryToken | undefined {
  if (!category) {
    return undefined;
  }
  return EDITORIAL_CATEGORY_TO_TOKEN[category as keyof typeof EDITORIAL_CATEGORY_TO_TOKEN];
}

/**
 * Runtime guard for validating category tokens from external sources.
 */
export function isCategoryToken(value: unknown): value is CategoryToken {
  return value === "women's" || value === "men's" || value === "kids" || value === "beauty";
}

/**
 * Maps display gender format to top-level category name
 * Used for navbar highlighting
 */
export function mapGenderToCategory(gender: string | undefined): string | null {
  if (!gender) return null;

  switch (gender) {
    case "Women's":
    case "women":
      return "Womenswear";
    case "Men's":
    case "men":
      return "Menswear";
    case "Kids":
    case "kids":
      return "Kidswear";
    case "Beauty":
      return "Beauty";
    case "Both (M+W)":
    case "all":
      return null; // No highlighting for "Both M+W"
    default:
      return null;
  }
}
